import { Table } from '@mantine/core';
import { ChangeEvent } from 'react';
import { AcquirerTypes } from '../../enums/acquirer-types.enum';
import { OpenComponent } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import { truncateString } from '../../helpers/common.helpers';
import { formatDate3LetterMonth } from '../../helpers/date-convert';
import useAuth from '../../hooks/useAuth';
import { BasicMerchant } from '../../type/admin.types';
import { SafeText } from '../safe-text/safe-text.component';
import XSkeletonLoadingTableBody from '../x-components/x-skeletons/x-skeleton-loading-table.component';
import './admin-table.styles.css';
import StatusDropdown from './components/status-dropdown.component';
import StatusText from './components/status-text.component';

interface Props {
  type: string;
  merchants: BasicMerchant[] | undefined;
  isLoading: boolean;
  isChecked: number[];
  setIsChecked: React.Dispatch<React.SetStateAction<number[]>>;
  checkedAll: boolean;
  setCheckedAll: React.Dispatch<React.SetStateAction<boolean>>;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  setSelectedUser: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const AdminTable = (props: Props) => {
  const {
    type,
    merchants,
    isLoading,
    isChecked,
    setIsChecked,
    checkedAll,
    setCheckedAll,
    setRefreshTable,
    setComponentManager,
    setSelectedUser,
  } = props;
  const { auth } = useAuth();
  const isViewOnly =
    auth?.role === Roles.SETTLEMENT || auth?.role === Roles.CHECKER;

  const handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    if (checked) {
      setIsChecked([...isChecked, Number(id)]);
    } else {
      setIsChecked(isChecked.filter((item) => item !== Number(id)));
    }
  };

  const handleCheckAll = () => {
    if (!merchants) {
      return;
    }
    setCheckedAll(!checkedAll);

    if (checkedAll) {
      setIsChecked([]);
    } else {
      setIsChecked(merchants.map((li) => li.merchantId));
    }
  };

  const navigateMerchant = (id: number) => {
    setSelectedUser(id);
    setComponentManager(OpenComponent.VIEW_USER);
  };

  return (
    <div className='admin-table'>
      <form>
        <Table
          highlightOnHover
          sx={{ '& thead tr th': { height: 50 } }}
          horizontalSpacing='md'
        >
          <thead>
            <tr className='table-header'>
              {!isViewOnly && (
                <th style={{ width: 30 }}>
                  <input
                    type='checkbox'
                    style={{ cursor: 'pointer' }}
                    onChange={handleCheckAll}
                    checked={checkedAll}
                    data-cy='selectAllMerchants'
                  />
                </th>
              )}
              <th>Company name</th>
              <th>Location</th>
              <th>Contact number</th>
              <th>Business type</th>
              <th>Date created</th>
              <th>Onboarding reference number</th>
              <th>Reseller name</th>
              {type !== 'onboarding' && <th>Status</th>}
              <th>AUB</th>
              <th>UNIONBANK</th>
              <th>MAYA</th>
            </tr>
          </thead>
          {isLoading ? (
            <XSkeletonLoadingTableBody numColumns={12} />
          ) : (
            <tbody>
              {merchants?.map((element, index) => (
                <tr
                  key={element.merchantId}
                  className={`table-row ${
                    !element.isActive ? 'disabled-merchant' : ''
                  }`}
                  data-cy={'row' + element.merchantId}
                >
                  {!isViewOnly && (
                    <td>
                      <input
                        data-cy={'checkbox' + element.merchantId}
                        type='checkbox'
                        style={{ cursor: 'pointer' }}
                        name=''
                        id={element.merchantId.toString()}
                        checked={isChecked.includes(element.merchantId)}
                        onChange={handleCheck}
                      />
                    </td>
                  )}

                  <td onClick={() => navigateMerchant(element.merchantId)}>
                    {truncateString(element.merchantName, 20)}
                  </td>
                  <td onClick={() => navigateMerchant(element.merchantId)}>
                    {element.businessAddress.city.toUpperCase()}
                  </td>
                  <td
                    onClick={() => navigateMerchant(element.merchantId)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {`+63 ${element.businessAddress.contactNumber}`}
                  </td>
                  <td onClick={() => navigateMerchant(element.merchantId)}>
                    {element.businessType}
                  </td>
                  <td onClick={() => navigateMerchant(element.merchantId)}>
                    {formatDate3LetterMonth(element.dateCreated.toString())}
                  </td>
                  <td onClick={() => navigateMerchant(element.merchantId)}>
                    {element.onboardingReferenceNumber}
                  </td>
                  <td>
                    <SafeText data={element.resellerName} />
                  </td>
                  {type !== 'onboarding' && (
                    <td>
                      {!isViewOnly ? (
                        <StatusDropdown
                          entity={element}
                          status={
                            element.isActive
                              ? element.onboardingStatus
                              : 'DISABLED'
                          }
                          setIsChecked={setIsChecked}
                          setRefreshTable={setRefreshTable}
                        />
                      ) : (
                        <StatusText
                          status={
                            element.isActive
                              ? element.onboardingStatus
                              : 'DISABLED'
                          }
                        />
                      )}
                    </td>
                  )}
                  <td>
                    {!isViewOnly ? (
                      <StatusDropdown
                        entity={element}
                        status={
                          element.isActive && element.acquirersStatus.aub
                            ? element.acquirersStatus.aub
                            : 'DISABLED'
                        }
                        setIsChecked={setIsChecked}
                        setRefreshTable={setRefreshTable}
                        acquirerColumn={AcquirerTypes.AUB}
                      />
                    ) : (
                      <StatusText
                        status={
                          element.isActive && element.acquirersStatus.aub
                            ? element.acquirersStatus.aub
                            : 'DISABLED'
                        }
                      />
                    )}
                  </td>
                  <td>
                    {!isViewOnly ? (
                      <StatusDropdown
                        entity={element}
                        status={
                          element.isActive && element.acquirersStatus.ub
                            ? element.acquirersStatus.ub
                            : 'DISABLED'
                        }
                        setIsChecked={setIsChecked}
                        setRefreshTable={setRefreshTable}
                        acquirerColumn={AcquirerTypes.UNIONBANK}
                      />
                    ) : (
                      <StatusText
                        status={
                          element.isActive && element.acquirersStatus.ub
                            ? element.acquirersStatus.ub
                            : 'DISABLED'
                        }
                      />
                    )}
                  </td>
                  <td>
                    {!isViewOnly ? (
                      <StatusDropdown
                        entity={element}
                        status={
                          element.isActive && element.acquirersStatus.maya
                            ? element.acquirersStatus.maya
                            : 'DISABLED'
                        }
                        setIsChecked={setIsChecked}
                        setRefreshTable={setRefreshTable}
                        acquirerColumn={AcquirerTypes.MAYA}
                      />
                    ) : (
                      <StatusText
                        status={
                          element.isActive && element.acquirersStatus.maya
                            ? element.acquirersStatus.maya
                            : 'DISABLED'
                        }
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </Table>
      </form>
    </div>
  );
};

export default AdminTable;
